import React, { useState, useEffect, useRef, DragEvent, useMemo } from 'react';
import { useGoogleDriveApi } from './googleDriveApi';
import { Button } from "@mui/material";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;

type Props = {
  serviceName?: string;  
  calendarDate?: Date;
  bookingId?: string;
  folderId?: string;
  onUploaded: (type: "video" | "file") => void;
};

export default function GoogleDriveUploader({serviceName, calendarDate, bookingId, folderId, onUploaded}: Props) {
  const { uploadToGoogleDrive } = useGoogleDriveApi();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isVideo = !!file?.type?.startsWith('video/');

  // 为本地文件生成预览 URL（图片/视频/PDF 都能用）
  const localPreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);

  //拖拽上传
  const onDragOver = (e: DragEvent<HTMLDivElement>) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  const handleChoose = () => inputRef.current?.click();

  // 生成 <ServiceName> <YYYYMMDD_HHmm> <original>
  function buildDriveName(originalName: string, serviceName?: string, calendarDate?: Date | string) {
    let dateStr: string | undefined;
  
    if (calendarDate) {
      const d = new Date(calendarDate);
      if (!isNaN(d.getTime())) {
        dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }
  
    const prefix = [serviceName, dateStr].filter(Boolean).join("_");
    return prefix ? `${prefix}_${originalName}` : originalName;
  }

  //文件上传
  const handleUpload = async () => {
    if (!file) return alert('Choose the file');
    try {
      setUploading(true);
      const finalName = buildDriveName(file.name, serviceName, calendarDate);
      const { fileId } = await uploadToGoogleDrive(file, finalName, bookingId, folderId);
      alert(`${finalName} uploaded`);
      if (file.type.startsWith("video/")) {
        onUploaded("video");
      } else {
        onUploaded("file");
      }

      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (e) {
      console.error(e);
      alert('Upload Fail');
    } finally {
      setUploading(false);
    }
  };

  // Prevent closing/reloading the page during video upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (uploading && isVideo) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    if (uploading && isVideo) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploading, isVideo]);
  
  // 上传框
  const boxStyle: React.CSSProperties = {
    border: '2px dashed #C7CCD6',
    borderRadius: 10,
    padding: 24,
    background: dragOver ? '#F7FAFF' : '#FBFCFD',
    display: 'grid',
    placeItems: 'center',
    transition: 'background .15s ease, border-color .15s ease',
    borderColor: dragOver ? '#8AB4F8' : '#C7CCD6',
    cursor: 'pointer',
    minHeight: 220,
    width: 560,
  };

  // Only videos can be previewed
  const renderPreview = () => {
    if (!file || !localPreview) {
      return (
        <>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#2F3A4A' }}>Upload a File</div>
          <div style={{ marginTop: 6, color: '#6B7483' }}>Drag and drop files here</div>
        </>
      );
    }
    // Only show preview for videos
    if (file.type.startsWith('video/')) {
      return <video src={localPreview} controls style={{ width: '100%', maxHeight: 320, borderRadius: 8 }} />;
    }
    // For all other file types, just show file name and type
    return (
      <div style={{ textAlign: 'center', color: '#2F3A4A' }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>{file.name}</div>
        <div style={{ marginTop: 6, color: '#6B7483' }}>{file.type || 'unknown type'}</div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 8 }}>Upload File</div>

      <div
        style={boxStyle}
        onClick={handleChoose}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {renderPreview()}

        <input
          ref={inputRef}
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          style={{ display: 'none' }}
        />
        <div style={{ marginTop: "8px", fontSize: "0.9rem", color: "#666" }}>
          All file types supported (up to 1GB). Only videos can be previewed.
        </div>
      </div>

      {/* 状态条 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
        <div style={{ flex: 1, color: '#6B7483', fontSize: 13 }}>
          {file ? `File Name: ${file.name}` : 'No file chosen'}
        </div>


        <Button
          onClick={handleUpload}
          disabled={uploading || !file}
          variant="contained"
          sx={{
            height: 36,
            px: 2,
            borderRadius: 2,
            fontWeight: 700,
            backgroundColor: "#0F62FE",
            "&:hover": {
              backgroundColor: "#0053D1",
            },
          }}
        >
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>

      {uploading && isVideo && (
        <div style={{
          marginTop: 12,
          padding: '10px 12px',
          background: '#FFF4E5',
          border: '1px solid #FEC78C',
          color: '#7A4F00',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600
        }}>
          Uploading video… Please do not close or refresh this window until the upload completes.
        </div>
      )}
    </div>
  );
}
